<template>
  <div>
    <TitleWrapper
      :filter-option="false"
      title="NOTICE_BOARD_POST_LABELS"
      tooltip-title="NOTICE_BOARD_POST_LABELS"
      :display-breadcrumb="true"
    />
    <Loader v-if="isLoading" class="mt-20 mb-40" :content="true" />
    <NoRecordFound v-else-if="!isLoading && isEmpty(postLabels)" />

    <TableWrapper v-else>
      <THead>
        <TRHead>
          <TH
            v-for="head in tableHeaders"
            v-show="showColumnInDataTable(filtersData, head.dataTableName, skipForFilterColumns)"
            :key="head.id"
          >
            {{ head.dataTableName }}
          </TH>
        </TRHead>
      </THead>
      <TBody>
        <TRBody v-for="(label, index) in postLabels" :key="index">
          <TD v-show="showColumnInDataTable(filtersData, 'Title', ['Title'])">
            {{ label?.title || GENERAL_CONSTANTS.NOT_APPLICABLE }}
          </TD>
          <TD v-show="showColumnInDataTable(filtersData, 'Description')">
            {{ label?.description || GENERAL_CONSTANTS.NOT_APPLICABLE }}
          </TD>
          <TD v-show="showColumnInDataTable(filtersData, 'Actions')">
            <TableAction
              :idx="index"
              :current-user="label"
              :action-list="actionList"
              @action="labelAction"
            />
          </TD>
        </TRBody>
      </TBody>
    </TableWrapper>
    <Pagination
      v-if="showPagination"
      v-show="!isLoading"
      :key="`Pagination${forceRender}`"
      :record-limit="filteredRecordLimit"
      :max-range="paginationCounts(labelsCounts, filteredRecordLimit)"
      @filterRecord="filterRecord"
    />
    <UIConfirmationModal
      heading="DELETE_POST_LABEL"
      message="Are you sure you want to delete "
      button-text="Delete"
      :modal="confirmation"
      :name="currentPostLabel?.title"
      remaining-message="Post Label ?"
      @cancel="toggleConfirmationModal"
      @confirm="deleteLabel"
    />
    <PostLabelModal
      v-if="isPostLabelModalDisplay"
      :modal="isPostLabelModalDisplay"
      :edit="currentPostLabel"
      @toggle="togglePostLabelModal"
    />
  </div>
</template>

<script>
import { isEmpty } from 'lodash'
import { mapActions, mapState } from 'vuex'
import TableAction from '@components/TableAction.vue'
import Loader from '@components/BaseComponent/Loader.vue'
import { paginationRangeHandler } from '@src/components/BaseComponent/pagination.util.js'
import fileMixins from '@/src/mixins/file-mixins'
import Pagination from '@components/BaseComponent/Pagination.vue'
import GeneralMixin from '@src/mixins/general-mixins.js'
import GENERAL_CONSTANTS from '@src/constants/general-constants'
import NoRecordFound from '@/src/components/BaseComponent/NoRecordFound.vue'
import scrollMixin from '@src/mixins/scroll-mixin'
import UIConfirmationModal from '@src/components/UiElements/UIConfirmationModal.vue'
import { dropdownTopBottom, objectDeepCopy, showColumnInDataTable } from '@utils/generalUtil.js'
import POST_LABELS_DATA from './post-labels.json'
import PostLabelModal from '@views/notice-board/modals/AddAndUpdatePostLabels.vue'
import {
  TableWrapper,
  TBody,
  TD,
  TH,
  THead,
  TRBody,
  TRHead,
} from '@src/components/UiElements/TableElements/index.js'
import TitleWrapper from '@components/TitleWrapper.vue'

export default {
  components: {
    TableAction,
    Loader,
    Pagination,
    NoRecordFound,
    UIConfirmationModal,
    PostLabelModal,
    TableWrapper,
    TBody,
    TD,
    TH,
    THead,
    TRBody,
    TRHead,
    TitleWrapper,
  },
  mixins: [GeneralMixin, fileMixins, scrollMixin],
  data() {
    return {
      isEmpty,
      GENERAL_CONSTANTS,
      forceRender: 0,
      skipForFilterColumns: POST_LABELS_DATA.postLabelSkipForFilterColumns,
      defaultSelectedColumns: POST_LABELS_DATA.postLabelDefaultSelectedColumns,
      tableHeaders: POST_LABELS_DATA.postLabelTableHeaders,
      filtersData: {},
      filteredRecordLimit: GENERAL_CONSTANTS.RECORD_LIMIT,
      showPagination: false,
      labelsCounts: 0,
      actionList: [{ name: 'Edit' }, { name: 'Delete' }],
      currentPostLabel: null,
      confirmation: false,
      isPostLabelModalDisplay: false,
      postLabels: null,
      isLoading: false,
    }
  },
  computed: {
    ...mapState({
      showModal: (state) => state.layout.showModal,
    }),
  },

  mounted() {
    this.setRightbarData()
    this.filterRecord()
  },
  methods: {
    showColumnInDataTable,
    dropdownTopBottom,
    togglePostLabelModal(payload) {
      this.isPostLabelModalDisplay = !this.isPostLabelModalDisplay
      if (!this.isPostLabelModalDisplay) this.currentPostLabel = null
      if (payload) {
        this.filterRecord()
        this.forceRender++
      }
    },
    setFilterData(filtersPayload) {
      this.filtersData = objectDeepCopy(filtersPayload)
      this.filterRecord()
    },
    toggleConfirmationModal() {
      this.confirmation = !this.confirmation
    },

    filterRecord(range) {
      this.isLoading = true
      const paginationLimit = paginationRangeHandler(range)
      let payload = {
        ...paginationLimit,
      }
      payload = this.filtersData ? { ...payload, ...{ $where: this.filtersData?.$where } } : payload
      this.getPostLabels(payload)
        .then((response) => {
          this.labelsCounts = response?.data.meta.total_records
          this.postLabels = response?.data?.records
          this.showPagination = this.labelsCounts > GENERAL_CONSTANTS.RECORD_LIMIT
          this.setRightbarData(this.labelsCounts)
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    labelAction(action, currentLabel, idx) {
      switch (action) {
        case 'Edit':
          this.currentPostLabel = currentLabel
          this.togglePostLabelModal()
          break
        case 'Delete':
          this.currentPostLabel = currentLabel
          this.toggleConfirmationModal()
          break
      }
    },

    deleteLabel() {
      this.deletePostLabel(this.currentPostLabel?.id).then(() => {
        this.setRightbarData(this.postLabels.length)
        this.currentPostLabel = null
        this.confirmation = false
        this.forceRender++
        this.filterRecord()
      })
    },
    setRightbarData(length = 0) {
      const rightBarContent = {
        header: {
          title: 'Labels',
          buttons: [
            {
              title: 'ADD_LABEL',
              classes: ['primary-button-right'],
              action: {
                func: () => {
                  this.togglePostLabelModal()
                },
              },
            },
          ],
        },
        stats: [
          {
            title: GENERAL_CONSTANTS.STATS_AND_SUMMARY,
            categories: [
              {
                text: 'TOTAL_LABELS',
                value: length,
                tooltipDescription: 'Total number of Post Labels',
              },
            ],
          },
        ],
      }
      this.setRightbarContent(rightBarContent)
    },
    ...mapActions('layout', ['setRightbarContent', 'setShowModal']),
    ...mapActions('noticeboard', ['getPostLabels', 'deletePostLabel']),
  },
}
</script>

<style lang="scss" module>
tr:nth-child(even) {
  background-color: var(--ghost-white);
}
</style>
